// @flow
import { filter, get, keyBy, noop, omit, without } from 'lodash';

import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  RESET_FRIENDS,
  VIEW_FRIEND,
  SET_CURRENT_GROUP
} from '../constants/actionTypes';
import {
  ACCEPT_FRIEND_REQUEST,
  CREATE_FRIEND_REQUEST,
  FRIENDS,
  REMOVE_FRIEND,
  REMOVE_FRIEND_REQUEST,
  SEARCH_USER,
  USER_SIGN_OUT,
  UPDATE_FRIEND_GROUPS
} from '../constants/apiTypes';
import { FRIEND } from '../constants/friendStatusTypes';
import decodeFriend from '../normalizers/decodeFriend';
import decodeUser from '../normalizers/decodeUser';
import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';

const initialState: FriendState = {
  hasInitialised: false,
  friends: {},
  currentFriend: null,
  errors: null,
  infoMessage: null,
  isFetchingFriends: true,
  savingFriends: new Set(),
  isDeletingFriend: false
};

function onFetchFriends(
  state: FriendState,
  action: {
    friendRequests: Object,
    friends: Object
  }
) {
  return {
    hasInitialised: true,
    isFetchingFriends: false,
    friends: keyBy(action.friends.map(decodeFriend), 'id')
  };
}

function onRemoveFriend(state: FriendState, action: { friendId: number }) {
  return {
    isDeletingFriend: false,
    friends: omit(state.friends, action.friendId),
    currentFriend: get(state.currentFriend, 'id') === action.friendId ? null : state.currentFriend
  };
}

function onSearchUsers(state: FriendSearchState, action: { users: Array<Object> }) {
  const users = action.users.map(decodeUser);
  const friends = new Set(users.filter(u => u.status === FRIEND).map(u => u.id));
  const newFriends = filter(state.friends, f => !friends.has(f.id));

  return {
    friends:
      newFriends.length > 0
        ? {
            ...state.friends,
            ...keyBy(
              newFriends.map(newFriend => ({
                ...newFriend,
                status: FRIEND
              })),
              'id'
            )
          }
        : state.friends
  };
}

function onRemoveFriendRequest(state: FriendState, action: { id: number }) {
  return {
    currentFriend: get(state.currentFriend, 'id') === action.id ? null : state.currentFriend
  };
}

function onAcceptFriendRequest(
  state: FriendState,
  action: {
    id: number,
    friend: Object
  }
) {
  const newFriend = decodeFriend(action.friend);

  // if we just accepted a friend request and we were looking
  // at the guy we accepted, replace the 'currentFriend' with the now friend object
  let nextCurrent = state.currentFriend;
  if (nextCurrent && nextCurrent.id === newFriend.id) {
    nextCurrent = newFriend;
  }
  return {
    friends: {
      ...state.friends,
      [newFriend.id]: newFriend
    },
    currentFriend: nextCurrent
  };
}

const apiRequestReducer = switchFunc(
  {
    [FRIENDS]: () => ({
      errors: null,
      isFetchingFriends: true
    }),
    [REMOVE_FRIEND]: () => ({
      errors: null,
      isDeletingFriend: true
    }),
    [UPDATE_FRIEND_GROUPS]: (state: FriendState, action) => ({
      errors: null,
      savingFriends: new Set([...state.savingFriends, action.friendId])
    })
  },
  () => noop
);

const apiSuccessReducer = switchFunc(
  {
    [FRIENDS]: onFetchFriends,
    [REMOVE_FRIEND]: onRemoveFriend,
    [REMOVE_FRIEND_REQUEST]: onRemoveFriendRequest,
    [ACCEPT_FRIEND_REQUEST]: onAcceptFriendRequest,
    [CREATE_FRIEND_REQUEST]: () => ({
      currentFriend: null
    }),
    [USER_SIGN_OUT]: () => initialState,
    [UPDATE_FRIEND_GROUPS]: (state: FriendState, action) => ({
      savingFriends: new Set(without(...state.savingFriends, action.friendId))
    }),
    [SEARCH_USER]: onSearchUsers
  },
  () => noop
);

const apiFailureReducer = switchFunc(
  {
    [FRIENDS]: (state, action) => ({
      errors: action.errors,
      isFetchinFriends: false
    }),
    [REMOVE_FRIEND]: (state, action) => ({
      errors: action.errors,
      isDeletingFriend: false
    }),
    [UPDATE_FRIEND_GROUPS]: (state: FriendState, action) => ({
      errors: action.errors,
      savingFriends: new Set(without(...state.savingFriends, action.friendId))
    })
  },
  () => noop
);

export default createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => ({
    ...apiRequestReducer(action.api)(state, action)
  }),
  [API_CALL_SUCCESS]: (state, action) => ({
    ...apiSuccessReducer(action.api)(state, action)
  }),
  [API_CALL_FAILURE]: (state, action) => ({
    ...apiFailureReducer(action.api)(state, action)
  }),
  [VIEW_FRIEND]: (state, action) => ({
    currentFriend: state.currentFriend === action.friend ? undefined : action.friend
  }),
  [RESET_FRIENDS]: () => initialState,
  [SET_CURRENT_GROUP]: () => ({
    // reset the friend if we change group
    // this only affects mobile but no need to keep it around
    // as we move around
    currentFriend: undefined
  })
});
